// composables
import { ref, watch, type Ref } from 'vue'
// composables
import useApiRequest from '@/composables/useApiRequest'

/**
 * Composable for managing customer timezone detection and updates
 * Automatically detects timezone from route coordinates and updates agreement
 */
export default function useAgreementTimezone(
  agreementID: Ref<string | number>,
  updateAgreementData: (data: Record<string, unknown>) => Promise<unknown>
) {
  // state
  const routeCoordinates = ref<{ start?: { lat?: number; lng?: number } }>({})
  const customerTimeZone = ref<string | null>(null)

  // api requests
  const timezoneApiRequest = useApiRequest()

  /**
   * Listens to changes in routeCoordinates and fetches timezone
   */
  watch(routeCoordinates, () => {
    if (customerTimeZone.value != null) {
      return
    }

    const lat = routeCoordinates.value.start?.lat
    const lng = routeCoordinates.value.start?.lng

    // Check that the lat and lng are valid
    if (lat == null || lng == null) {
      return
    }

    timezoneApiRequest
      .send<{ timeZone: string }>({ endpoint: '/v1/utilities/timezone?lat=' + lat + '&lng=' + lng })
      .then(response => {
        if (response.success && response.data) {
          customerTimeZone.value = response.data.timeZone

          // Update timeZone in database
          updateAgreementData({ timeZone: customerTimeZone.value })
        }
      })
  })

  /**
   * Sets the customer timezone (typically from agreement data)
   * @param timezone - The timezone string to set
   */
  function setCustomerTimeZone(timezone: string | null): void {
    customerTimeZone.value = timezone
  }

  return {
    // state
    routeCoordinates,
    customerTimeZone,
    // methods
    setCustomerTimeZone
  }
}
